import { UnknownObject } from '@utils/types';
import { JSONSchema } from '@components/common/jsonSchemaForm/jsonSchemaForm';
import { ISourceDefinition } from '@stores/sourceDefinitionsList';
import { Resource } from '../cloudSource/cloudSourceSettings';
import { cloneDeep, dropRight, get, last, merge, pullAll, set, unset } from 'lodash';
import { singerGoogleAdsUISchema, singerRecurlyUISchema } from './sourceSpecificUiSchema';

const generateOauthUiSchemaWithAuthSpec = (
  sourceDefinition: Pick<ISourceDefinition, 'name' | 'displayName' | 'options'>,
  authSpec: AuthSpec | AdvancedAuth,
) => {
  if (!authSpec) {
    return {};
  }
  const accountWidget = {
    'ui:widget': 'Oauth',
    'ui:options': {
      label: false,
      role: sourceDefinition.options?.auth?.oauthRole || sourceDefinition.name,
      displayName: sourceDefinition.displayName,
      provider: sourceDefinition.options?.auth?.provider,
      paramsFromConfig: [] as string[][],
    },
  };
  const uiSchema = {};
  const { oauth2Specification } = authSpec as AuthSpec;
  let rootPath: string[] = [];
  if (oauth2Specification) {
    rootPath = oauth2Specification.rootObject.filter((v) => Number.isNaN(Number(v)));
  } else {
    const { predicate_key, oauth_config_specification } = authSpec as AdvancedAuth;
    const { oauth_user_input_from_connector_config_specification } = oauth_config_specification;
    // some connectors requires specific params for the oauth flow
    // for example salesforce requires is_sandbox value because oauth URLS
    if (oauth_user_input_from_connector_config_specification) {
      accountWidget['ui:options'].paramsFromConfig = getOauthConfigPathFromSpec(
        oauth_user_input_from_connector_config_specification,
      );
    }
    rootPath = dropRight(predicate_key, 1);
    set(uiSchema, predicate_key, {
      'ui:widget': 'hidden',
    });
  }
  set(uiSchema, [...rootPath, 'accountId'], accountWidget);
  return uiSchema;
};

const generateOauthUiSchema = (
  schema: JSONSchema,
  sourceDefinition: Pick<ISourceDefinition, 'name' | 'displayName' | 'options'>,
  authSpec?: AuthSpec | AdvancedAuth,
) => {
  const schemaProperties = schema.properties || {};
  if (authSpec) {
    return generateOauthUiSchemaWithAuthSpec(sourceDefinition, authSpec);
  }
  return Object.keys(schemaProperties).reduce((uiSchema, propName) => {
    if (propName === 'accountId') {
      uiSchema[propName] = {
        'ui:widget': 'Oauth',
        'ui:options': {
          role: sourceDefinition.options?.auth?.oauthRole || sourceDefinition.name,
          displayName: sourceDefinition.displayName,
          provider: sourceDefinition.options?.auth?.provider,
        },
      };
    }
    return uiSchema;
  }, {} as Record<string, Record<string, unknown>>);
};
export interface AuthSpec {
  auth_type: 'oauth2.0';
  oauth2Specification: {
    /*
     A list of strings representing a pointer to the root object which
     contains any oauth parameters in the ConnectorSpecification.
          Examples:
          if oauth parameters were contained inside the top level, rootObject=[]
          If they were nested inside another object
           {'credentials': {'app_id' etc...}, rootObject=['credentials']
          If they were inside a oneOf
          {'switch': {oneOf: [{client_id...}, {non_oauth_param]}},  rootObject=['switch', 0]
          "
    */
    rootObject: string[];
    /*
      "Pointers to the fields in the rootObject needed to obtain the initial
      refresh/access tokens for the OAuth flow.
      Each inner array represents the path in the rootObject of the referenced field.
      For example.
        Assume the rootObject contains params 'app_secret', 'app_id'
        which are needed to get the initial refresh token.
        If they are not nested in the rootObject,
          then the array would look like this [['app_secret'], ['app_id']]
        If they are nested inside an object called 'auth_params'
          then this array would be [['auth_params', 'app_secret'], ['auth_params', 'app_id']]"
    */
    oauthFlowInitParameters: string[][];
    /*
    "Pointers to the fields in the rootObject which can be populated from successfully
    completing the oauth flow using the init parameters.
    This is typically a refresh/access token.
    Each inner array represents the path in the rootObject of the referenced field."
    */
    oauthFlowOutputParameters: string[][];
  };
}
interface AdvancedAuthSpec {
  properties: {
    [i: string]: {
      path_in_connector_config: string[];
    };
  };
}
export interface AdvancedAuth {
  auth_flow_type: 'oauth2.0' | 'oauth1.0';
  /*
    Json Path to a field in the connectorSpecification that
    should exist for the advanced auth to be applicable.
      Example: ['credentials', 'auth_type']
  */
  predicate_key: string[];
  /*
     Value of the predicate_key fields for the advanced auth to be applicable.
      Example 'oauth2.0'
  */
  predicate_value: string;
  oauth_config_specification: {
    /*
    OAuth specific blob. This is a Json Schema used to validate Json configurations
    produced by the OAuth flows as they are returned by the distant OAuth APIs.
    For each field, a special annotation `path_in_connector_config`
    can be specified to determine where to merge it,
      Examples:
          complete_oauth_output_specification={
            refresh_token: {
              type: string,
              path_in_connector_config: ['credentials', 'refresh_token']
            }
          }
    */
    complete_oauth_output_specification: AdvancedAuthSpec;
    /*
    This is a Json Schema used to validate Json configurations persisted
    as Airbyte Server configurations that also need to be merged back into the connector
    configuration at runtime.
    For each field, a special annotation `path_in_connector_config` exists, that
    determine where to merge it,
      Examples:
            complete_oauth_server_output_specification={
              client_id: {
                type: string,
                path_in_connector_config: ['credentials', 'client_id']
              },
              client_secret: {
                type: string,
                path_in_connector_config: ['credentials', 'client_secret']
              }
            }
    */
    complete_oauth_server_output_specification: AdvancedAuthSpec;
    /*
    These are input values the user is entering through the UI to authenticate to
    the connector, that might also shared as inputs for syncing data via the connector
      Examples:
      oauth_user_input_from_connector_config_specification={
              is_sandbox: {
                type: string
                path_in_connector_config: ['is_sandbox']
              }
            }
    */
    oauth_user_input_from_connector_config_specification: AdvancedAuthSpec;
  };
}
const getOauthConfigPathFromSpec = (authSpec: AdvancedAuthSpec) =>
  Object.keys(authSpec?.properties || {}).map(
    (k) => authSpec.properties[k].path_in_connector_config,
  );

const setOAuthAccountIdToSchema = (
  credentialsObject: JSONSchema,
  oauthParametersPaths: string[][],
) => {
  let accountOrder: number | undefined;
  // Remove Oauth properties like clientId and clientSecret from the schema
  oauthParametersPaths.forEach((path) => {
    const propSchema = get(credentialsObject?.properties, path);
    // get the lowest order number of the properties we will replace with account id
    if (
      typeof propSchema?.order === 'number' &&
      (accountOrder === undefined || propSchema.order < accountOrder)
    ) {
      accountOrder = propSchema.order;
    }
    unset(credentialsObject?.properties, path);
  });

  // add accountId property in schema
  credentialsObject.properties = merge(
    {
      accountId: {
        type: 'string',
        order: credentialsObject.order || accountOrder,
      },
    },
    credentialsObject?.properties || {},
  );

  // make sure if a const exist, to set the default value
  Object.keys(credentialsObject.properties).every((key) => {
    const current = credentialsObject?.properties![key];
    if (current.const) {
      current.default = current.default || current.const;
      return false;
    }
    return true;
  });
  const oauthParametersNames = oauthParametersPaths.map((p: string[]) => last(p) as string);
  // Remove Oauth fields from required
  credentialsObject!.required = pullAll(
    (credentialsObject?.required || []) as string[],
    oauthParametersNames,
  );
  // Add accountId in required fields
  credentialsObject!.required.push('accountId');
};
/*
  transformOAuthJSONSchemaWithAdvanceAuth
  Transforming the schema for OAuth, using the advanced_auth property,
  that is part of the connector's spec https://github.com/airbytehq/airbyte/blob/1d1476e4932039dcee7a50a96d039f48f945893c/airbyte-protocol/models/src/main/resources/airbyte_protocol/airbyte_protocol.yaml#L388
*/
const transformOAuthJSONSchemaWithAdvanceAuth = (
  JSONschema: JSONSchema,
  authSpec?: AdvancedAuth,
) => {
  const schema = cloneDeep(JSONschema);

  if (!authSpec) {
    return {};
  }
  const { oauth_config_specification, predicate_key, predicate_value } = authSpec;

  const oauthParametersClientPaths = getOauthConfigPathFromSpec(
    oauth_config_specification.complete_oauth_output_specification,
  );
  const oauthParametersServerPaths = getOauthConfigPathFromSpec(
    oauth_config_specification.complete_oauth_server_output_specification,
  );
  const oauthParametersPaths = [...oauthParametersClientPaths, ...oauthParametersServerPaths].map(
    (p) => [last(p)!],
  );
  const authTypeKey = [last(predicate_key) as string];
  const rootPath = dropRight(predicate_key, 1);
  const rootObject = (get(schema.properties, rootPath) as JSONSchema) || schema;
  let credentialsObject = rootObject || {};

  if (rootObject?.oneOf) {
    credentialsObject =
      (rootObject as JSONSchema)?.oneOf?.find(
        (obj) => get(obj, `properties.${authTypeKey.join('.')}.const`) === predicate_value,
      ) || ({} as JSONSchema);
  }
  setOAuthAccountIdToSchema(credentialsObject, oauthParametersPaths);
  return schema;
};
/* transformOAuthJSONSchemaWithAuthSpec
  Transforming the schema for OAuth using authSpecification property,
  that is part of connector's spec. The authSpecification property, as it's defined
  in the airbyte protocol https://github.com/airbytehq/airbyte/blob/1d1476e4932039dcee7a50a96d039f48f945893c/airbyte-protocol/models/src/main/resources/airbyte_protocol/airbyte_protocol.yaml#L378
  is deprecated, but some connectors are not updated to include the new advanced_auth property.
*/
const transformOAuthJSONSchemaWithAuthSpec = (JSONschema: JSONSchema, authSpec?: AuthSpec) => {
  const schema = cloneDeep(JSONschema);
  if (!authSpec) {
    return {};
  }
  const { rootObject, oauthFlowOutputParameters, oauthFlowInitParameters } =
    authSpec.oauth2Specification;
  // get path removing any number that is for oneOf
  const rootObjectPath = rootObject.filter((v) => Number.isNaN(Number(v)));
  // get all oauth properties paths
  const oauthParametersPath = [...oauthFlowOutputParameters, ...oauthFlowInitParameters];
  const schemaProperties = schema.properties || {};
  const oauthPropertiesObject = get(schemaProperties, rootObjectPath) || schema;

  let propertiesToReplace: JSONSchema = oauthPropertiesObject;

  if (oauthPropertiesObject.oneOf) {
    // get the index on the OAuth json schema
    const oneOfOauthIndexPath = [Number(last(rootObject))];
    propertiesToReplace = get(propertiesToReplace, ['oneOf', ...oneOfOauthIndexPath]);
  }
  setOAuthAccountIdToSchema(propertiesToReplace, oauthParametersPath);
  return schema;
};

export const transformJSONSchema = (jsonSchema: JSONSchema, authSpec?: AuthSpec | AdvancedAuth) => {
  if (authSpec) {
    const { oauth2Specification } = authSpec as AuthSpec;
    if (oauth2Specification) {
      return transformOAuthJSONSchemaWithAuthSpec(jsonSchema, authSpec as AuthSpec) as JSONSchema;
    }
    return transformOAuthJSONSchemaWithAdvanceAuth(
      jsonSchema,
      authSpec as AdvancedAuth,
    ) as JSONSchema;
  }
  const schemaProperties = jsonSchema.properties || {};
  const required = jsonSchema.required || [];
  const newRequiredFields = [] as string[];
  const properties = Object.keys(schemaProperties).reduce((accum, key) => {
    const currentProp = schemaProperties[key];
    if (key === 'credentials' && currentProp.type === 'object') {
      accum.accountId = {
        type: 'string',
        title: currentProp.title,
        order: currentProp.order,
      };
      newRequiredFields.push('accountId');
    } else {
      accum[key] = currentProp;
      if (required.includes(key)) {
        newRequiredFields.push(key);
      }
    }
    return accum;
  }, {} as UnknownObject);
  return {
    ...jsonSchema,
    required: newRequiredFields,
    properties,
  } as JSONSchema;
};

/*
 * Returns sources specific ui schema for react-jsonschema-form
 * https://react-jsonschema-form.readthedocs.io/en/latest/
 */
const generateSourceSpecificUISchema = (
  schema: JSONSchema,
  sourceDefinition: Pick<ISourceDefinition, 'name' | 'displayName' | 'options'>,
) => {
  const schemaProperties = schema.properties || {};
  if (sourceDefinition.name === 'singer-google_adwords') {
    return singerGoogleAdsUISchema(schemaProperties);
  }
  if (sourceDefinition.name === 'singer-recurly') {
    return singerRecurlyUISchema(schemaProperties);
  }
  return {};
};

export const generateUiSchema = (
  schema: JSONSchema,
  sourceDefinition: Pick<ISourceDefinition, 'name' | 'displayName' | 'options'>,
  authSpec?: AuthSpec | AdvancedAuth,
) => {
  const oauthUISchema = generateOauthUiSchema(schema, sourceDefinition, authSpec);
  const sourceSpecificSchema = generateSourceSpecificUISchema(schema, sourceDefinition);
  return {
    ...oauthUISchema,
    ...sourceSpecificSchema,
  };
};
export const transformResourceList = (resources: string[]): Resource[] => {
  const resourceList = resources.map((resource) => ({
    id: resource,
    config: {},
  }));
  return resourceList;
};
